import { array } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import { or } from 'ember-truth-helpers';
import { trackedFunction } from 'reactiveweb/function';

import { findRecord } from 'umg-ember-backend/data/builders/umg-rest';
import { imageUrl } from 'umg-ember-backend/helpers/image-url';
import type FlashMessagesService from 'umg-ember-backend/services/flash-messages';
import { getBem } from 'umg-ember-backend/utils/get-bem';
import { slugify } from 'umg-ember-backend/utils/mlp-slugify';

import styles from './styles.module.css';

const bem = getBem(styles);

interface DashboardArticleSignature {
  Args: {
    article: TODO;
    domain: string;
  };
  Element: HTMLElement;
}

export default class DashboardArticleComponent extends Component<DashboardArticleSignature> {
  // Services
  @service declare store: TODO;
  @service declare flashMessages: FlashMessagesService;

  // Getter and setter
  get article() {
    return this.loadArticle.value?.article;
  }

  get asset() {
    return this.loadArticle.value?.asset;
  }

  get url() {
    const article = this.article;

    if (!article) {
      return;
    }

    const domain = this.args.domain.replace(/\/+$/, '');
    const baseUrl = /^https?:\/\//i.test(domain) ? domain : `https://${domain}`;
    const slug = slugify(`${article.headline}-${article.id}`);

    return `${baseUrl}/articles/${slug}`;
  }

  // Functions
  loadArticle = trackedFunction(this, async () => {
    try {
      const { content } = await this.store.request(
        findRecord('article', this.args.article.contentItemId),
      );
      const article = content.data;

      const asset = await article.assetId;

      return { article, asset };
    } catch (error) {
      this.flashMessages.serverError(error);
    }
  });

  // Template
  <template>
    <article class={{(bem)}} data-test-article ...attributes>
      {{#if this.article}}
        {{#if this.asset}}
          <div class={{bem 'images'}} data-test-article-images>
            <img
              alt={{this.asset.headline}}
              class={{bem 'image'}}
              data-test-article-image
              src={{imageUrl (array this.asset '990/1024')}}
            />
          </div>
        {{/if}}
        <header class={{bem 'header'}} data-test-article-header>
          <h2 class={{bem 'headline'}} data-test-article-headline>
            <a
              href={{this.url}}
              target='_blank'
              rel='noopener noreferrer'
              data-test-article-link
            >
              {{or this.article.teaserHeadline this.article.headline}}
            </a>
          </h2>
        </header>
        <div class={{bem 'content'}} data-test-article-content>
          {{! template-lint-disable no-triple-curlies }}
          {{#if this.article.teaserText}}
            {{{this.article.teaserText}}}
          {{else}}
            {{{this.article.text}}}
          {{/if}}
          {{! template-lint-enable no-triple-curlies }}
        </div>
      {{/if}}
    </article>
  </template>
}
